<div class="sms-gateway-container settings-page">

  <div class="settings-page__content">
    <p class="settings-page__description">
      {{ 'SETTINGS_SMS.GATEWAY.PAGE_DESCRIPTION' | translate }}
    </p>
    <p-tabs [value]="activeTab()" (valueChange)="onTabChange('' + $event)">
      <p-tablist>
        <p-tab value="configuration">
          {{ 'SETTINGS_SMS.GATEWAY.GATEWAY_CONFIGURATION' | translate }}
        </p-tab>
        <p-tab value="report">
          {{ 'SETTINGS_SMS.GATEWAY.REPORT' | translate }}
        </p-tab>
      </p-tablist>

      <p-tabpanels>
        <p-tabpanel value="configuration">
          <div class="gateway-configuration-tab">
            @if (gatewayInitialLoadFailed() && gatewayLoadError(); as error) {
              <tf-settings-load-error
                [title]="error"
                [message]="'SETTINGS.LOAD_ERROR.MESSAGE' | translate"
                [details]="gatewayLoadErrorDetails()"
                [dense]="true"
                [retrying]="isLoading()"
                (retry)="reloadGateways()" />
            } @else if (!hasLoadedGatewaysOnce()) {
              <tf-settings-loading-placeholder [rows]="4" [columns]="4" />
            } @else if (showConfigurationEmptyState()) {
              <tf-settings-inline-message
                [message]="gatewayActionError()"
                messageId="sms-gateway-action-error"
                (dismissed)="clearGatewayActionError()" />

              <tf-empty-state
                [title]="'SETTINGS_SMS.GATEWAY.EMPTY.TITLE' | translate"
                [body]="'SETTINGS_SMS.GATEWAY.EMPTY.BODY' | translate">
                <svg
                  empty-state-illustration
                  class="settings-empty-illustration sms-empty-illustration"
                  width="96"
                  height="96"
                  viewBox="0 0 100 100"
                  fill="none"
                  aria-hidden="true">
                  <rect
                    class="sms-empty-illustration__phone"
                    x="26"
                    y="15"
                    width="38"
                    height="70"
                    rx="9" />
                  <path class="sms-empty-illustration__screen" d="M34 28H56M34 38H50M34 48H56" />
                  <path
                    class="sms-empty-illustration__signal"
                    d="M69 32C75 38 78 45 78 53S75 68 69 74" />
                  <path
                    class="sms-empty-illustration__signal"
                    d="M75 24C83 32 87 42 87 53S83 74 75 82" />
                  <circle class="sms-empty-illustration__home" cx="45" cy="74" r="3" />
                  <path
                    class="sms-empty-illustration__bubble"
                    d="M52 54H76C80 54 83 57 83 61V66C83 70 80 73 76 73H65L57 80V73H52C48 73 45 70 45 66V61C45 57 48 54 52 54Z" />
                  <path class="sms-empty-illustration__bubble-line" d="M56 62H72M56 68H66" />
                </svg>
                <p-button
                  empty-state-actions
                  icon="pi pi-plus"
                  [label]="'SETTINGS_SMS.GATEWAY.EMPTY.TWILIO_ACTION' | translate"
                  [disabled]="!canCreateSmsGateway()"
                  [pTooltip]="canCreateSmsGateway() ? '' : ('GENERAL.NO_PERMISSION' | translate)"
                  tooltipPosition="top"
                  (onClick)="openTwilioConfiguration()" />
                <p-button
                  empty-state-actions
                  icon="pi pi-plus"
                  severity="secondary"
                  [label]="'SETTINGS_SMS.GATEWAY.EMPTY.CUSTOM_ACTION' | translate"
                  [disabled]="!canCreateSmsGateway()"
                  [pTooltip]="canCreateSmsGateway() ? '' : ('GENERAL.NO_PERMISSION' | translate)"
                  tooltipPosition="top"
                  (onClick)="openCustomConfiguration()" />
                <p-button
                  empty-state-actions
                  icon="pi pi-refresh"
                  severity="secondary"
                  [loading]="isLoading()"
                  [disabled]="isLoading()"
                  [ariaLabel]="'GENERAL.REFRESH' | translate"
                  (onClick)="reloadGateways()" />
              </tf-empty-state>
            } @else {
              <tf-settings-list-header
                [showSearch]="false"
                [loading]="isLoading()"
                (refresh)="reloadGateways()">
                <p-button
                  headerActions
                  [label]="'SETTINGS_SMS.GATEWAY.ADD_TWILIO_CONFIGURATION' | translate"
                  severity="primary"
                  icon="pi pi-plus"
                  [disabled]="!canCreateSmsGateway()"
                  [pTooltip]="canCreateSmsGateway() ? '' : ('GENERAL.NO_PERMISSION' | translate)"
                  tooltipPosition="top"
                  (onClick)="openTwilioConfiguration()" />
                <p-button
                  headerActions
                  [label]="'SETTINGS_SMS.GATEWAY.ADD_CUSTOM_CONFIGURATION' | translate"
                  severity="primary"
                  icon="pi pi-plus"
                  [disabled]="!canCreateSmsGateway()"
                  [pTooltip]="canCreateSmsGateway() ? '' : ('GENERAL.NO_PERMISSION' | translate)"
                  tooltipPosition="top"
                  (onClick)="openCustomConfiguration()" />
              </tf-settings-list-header>

              <tf-settings-inline-message
                [message]="gatewayActionError()"
                messageId="sms-gateway-action-error"
                (dismissed)="clearGatewayActionError()" />

              <tf-data-table
                [columns]="configColumns()"
                [value]="configurations()"
                [loading]="false"
                [reorderable]="true"
                styleClass="p-datatable-sm"
                dataKey="id"
                emptyMessageKey="SETTINGS_SMS.GATEWAY.NO_CONFIGURATIONS_FOUND"
                (rowReorder)="onRowReorder($event)"
                (actionClick)="onConfigAction($event)">
                <ng-template tfColumnDef="priority" let-config>
                  <p-tag [value]="getStatusLabel(config)" [severity]="getStatusSeverity(config)" />
                </ng-template>
                <ng-template tfColumnDef="type" let-config>
                  {{ getTypeLabel(config.type) }}
                </ng-template>
                <ng-template tfColumnDef="endpoint" let-config>
                  {{ getEndpoint(config) }}
                </ng-template>
              </tf-data-table>
            }
          </div>
        </p-tabpanel>

        <p-tabpanel value="report">
          <div class="report-tab">
            @if (reportInitialLoadFailed() && reportLoadError(); as error) {
              <tf-settings-load-error
                [title]="error"
                [message]="'SETTINGS.LOAD_ERROR.MESSAGE' | translate"
                [details]="reportLoadErrorDetails()"
                [dense]="true"
                [retrying]="isReportLoading()"
                (retry)="reloadReport()" />
            } @else if (!hasLoadedReportsOnce()) {
              <tf-settings-loading-placeholder [rows]="5" [columns]="5" />
            } @else {
              <tf-settings-sms-report-filters
                [fromDate]="reportFilterValues().fromDate"
                [toDate]="reportFilterValues().toDate"
                [status]="reportFilterValues().status"
                [hasActiveFilters]="hasActiveReportFilters()"
                [isLoading]="isReportLoading()"
                (filterApplied)="onReportFilterApplied($event)"
                (filterCleared)="onReportFilterCleared()"
                (refresh)="reloadReport()" />

              <tf-settings-inline-message
                [message]="reportActionError()"
                messageId="sms-report-action-error"
                (dismissed)="clearReportActionError()" />

              <tf-data-table
                [columns]="reportColumns"
                [value]="smsReports()"
                [loading]="false"
                [paginator]="true"
                [rows]="50"
                [lazy]="true"
                [totalRecords]="totalReportRecords()"
                [currentPageReportTemplate]="'SETTINGS_SMS.REPORT.PAGINATION_MESSAGE' | translate"
                styleClass="p-datatable-sm"
                [sortField]="reportSortField() ?? 'scheduledAt'"
                [sortOrder]="reportSortOrder()"
                dataKey="id"
                emptyMessageKey="SETTINGS_SMS.REPORT.NO_SMS_FOUND"
                (sorted)="onReportSort($event)"
                (paged)="onReportPageChange($event)">
                <ng-template tfColumnDef="message" let-sms>
                  <span class="message-cell" [pTooltip]="sms.message" tooltipPosition="top">
                    {{
                      sms.message.length > 50 ? sms.message.substring(0, 50) + '...' : sms.message
                    }}
                  </span>
                </ng-template>
                <ng-template tfColumnDef="status" let-sms>
                  <p-tag [value]="sms.status" [severity]="getStatusBadgeSeverity(sms.status)" />
                </ng-template>
                <ng-template tfColumnDef="failureReason" let-sms>
                  <span
                    class="failure-reason-cell"
                    [pTooltip]="sms.failureReason"
                    tooltipPosition="top">
                    {{
                      sms.failureReason
                        ? sms.failureReason.length > 30
                          ? sms.failureReason.substring(0, 30) + '...'
                          : sms.failureReason
                        : '—'
                    }}
                  </span>
                </ng-template>
              </tf-data-table>
            }
          </div>
        </p-tabpanel>
      </p-tabpanels>
    </p-tabs>
  </div>

  <!-- Twilio Configuration Dialog -->
  <tf-settings-twilio-config-dialog
    [visible]="isTwilioDialogVisible()"
    (visibleChange)="onTwilioDialogVisibleChange($event)"
    [initialName]="editingConfiguration()?.name ?? ''"
    [initialEnabled]="editingConfiguration()?.enabled ?? true"
    [initialConfig]="twilioInitialConfig()"
    [onSave]="handleTwilioSave" />

  <!-- Custom Configuration Dialog -->
  <tf-settings-custom-sms-config-dialog
    [visible]="isCustomDialogVisible()"
    (visibleChange)="onCustomDialogVisibleChange($event)"
    [initialName]="editingConfiguration()?.name ?? ''"
    [initialEnabled]="editingConfiguration()?.enabled ?? true"
    [initialConfig]="customInitialConfig()"
    [initialParameters]="customInitialParameters()"
    [onSave]="handleCustomSave" />

  <!-- Test SMS Dialog -->
  <tf-settings-test-sms-dialog
    [visible]="isTestSmsDialogVisible()"
    (visibleChange)="onTestSmsDialogVisibleChange($event)"
    [logEntries]="testLogEntries()"
    [testInProgress]="testInProgress()"
    (send)="onTestSmsSend($event)" />
</div>
